/**
 * RP recycling line. MASTER_PROMPT section 8.2.
 *
 * The line is the plant's margin: every point of RP substitution displaces
 * virgin PP at roughly a third the cost. It is also where the material
 * balance leaks — Phase 0 traced 6,143 kg of the 7,163 kg unexplained to the
 * three Hikon granulators alone, which is why this board is built to name a
 * machine rather than report a total.
 */

import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api";
import { SheetHead, SheetToolbar } from "../../components/Sheet";
import { useSheet, type SheetColumn } from "../../lib/sheet";
import type { RecyclingBoard } from "../../lib/types";
import { fmtPct, fmtQty, toNumber } from "../../lib/format";

type MachineRow = RecyclingBoard["machines"][number];

const MACHINE_COLUMNS: SheetColumn<MachineRow>[] = [
  { key: "machine", label: "Machine", value: (r) => r.machine },
  { key: "stage", label: "Stage",
    value: (r) => r.stage.replace(/_/g, " ").toLowerCase() },
  { key: "runs", label: "Runs", type: "number", numeric: true,
    value: (r) => r.runs },
  { key: "input", label: "Input", type: "number", numeric: true,
    value: (r) => toNumber(r.input_qty), format: (r) => fmtQty(r.input_qty, 0) },
  { key: "output", label: "Output", type: "number", numeric: true,
    value: (r) => toNumber(r.output_qty),
    format: (r) => fmtQty(r.output_qty, 0) },
  { key: "yield", label: "Yield", type: "number", numeric: true,
    value: (r) => (r.yield_pct === null ? null : toNumber(r.yield_pct)),
    format: (r) => (r.yield_pct === null ? "—" : fmtPct(r.yield_pct)) },
  { key: "declared", label: "Declared loss", type: "number", numeric: true,
    value: (r) => toNumber(r.declared_loss),
    format: (r) => fmtQty(r.declared_loss, 0) },
  { key: "unexplained", label: "Unexplained", type: "number", numeric: true,
    value: (r) => toNumber(r.variance_qty),
    format: (r) => fmtQty(r.variance_qty, 0) },
  { key: "kg_per_hour", label: "kg/hour", type: "number", numeric: true,
    value: (r) => (r.kg_per_hour === null ? null : toNumber(r.kg_per_hour)),
    format: (r) => (r.kg_per_hour === null ? "—" : fmtQty(r.kg_per_hour, 0)) },
];
import { BarRow, BoardPage, DailyBars, Exceptions, KpiCard, day } from "./board";
import { comparisonLabel } from "../../lib/period";
import { useCompare, useReportPeriod } from "../../lib/reportPeriod";

export function Recycling() {
  const [period, setPeriod] = useReportPeriod();
  const [compare] = useCompare();
  const [board, setBoard] = useState<RecyclingBoard | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setBoard(null);
    setError(null);
    api
      .recyclingBoard(period, compare)
      .then(setBoard)
      .catch((e: ApiError) => setError(e.message));
  }, [period, compare]);

  useEffect(load, [load]);

  const machines = useSheet(board?.machines ?? [], MACHINE_COLUMNS);

  const mixMax = board
    ? Math.max(...board.input_mix.map((m) => Number(m.qty)), 1)
    : 1;
  const lossMax = board
    ? Math.max(...board.machines.map((m) => Number(m.variance_qty)), 1)
    : 1;

  return (
    <BoardPage
      title="RP recycling line"
      subtitle="Scrap in, recycled granule out — and where the kilograms go"
      period={period}
      onPeriod={setPeriod}
      shifts
      loading={!board}
      error={error}
    >
      {board && (
        <>
          <div className="kpirow">
            {board.kpis.map((k) => (
              <KpiCard key={k.key} kpi={k} vs={comparisonLabel(period, compare)} />
            ))}
          </div>

          <Exceptions items={board.exceptions} />

          <div className="board2">
            <section className="card">
              <h3 className="section-title">What the line ate</h3>
              <p className="section-note">
                The mix is the story of a recycling line — the legacy MIS
                reported one pooled figure
              </p>
              {board.input_mix.length === 0 ? (
                <p className="section-note">
                  No material issued to the line in this period.
                </p>
              ) : (
                <div className="heat">
                  {board.input_mix.map((m) => (
                    <BarRow
                      key={m.item_code}
                      label={m.item_name}
                      value={Number(m.qty)}
                      max={mixMax}
                      display={`${fmtQty(m.qty, 0)} kg`}
                      sub={m.share_pct === null ? "—" : fmtPct(m.share_pct)}
                    />
                  ))}
                </div>
              )}
            </section>

            <section className="card">
              <h3 className="section-title">Unexplained, by machine</h3>
              <p className="section-note">
                Beyond the loss each stage declared — the figure that names
                where to walk
              </p>
              {board.machines.every((m) => Number(m.variance_qty) === 0) ? (
                <p className="section-note">
                  Every stage came out within the loss it declared.
                </p>
              ) : (
                <div className="heat">
                  {board.machines
                    .filter((m) => Number(m.variance_qty) !== 0)
                    .sort(
                      (a, b) =>
                        Number(b.variance_qty) - Number(a.variance_qty)
                    )
                    .map((m) => (
                      <BarRow
                        key={m.machine}
                        label={m.machine}
                        value={Number(m.variance_qty)}
                        max={lossMax}
                        tone="negative"
                        display={`${fmtQty(m.variance_qty, 0)} kg`}
                        sub={
                          m.variance_pct === null
                            ? "—"
                            : fmtPct(m.variance_pct)
                        }
                      />
                    ))}
                </div>
              )}
            </section>
          </div>

          <section className="card">
            <h3 className="section-title">Output through the period</h3>
            <p className="section-note">
              Daily output in kilograms — a flat run of days is a line that
              stopped, not a line that was slow
            </p>
            <DailyBars
              rows={board.daily.map((d) => ({
                date: d.business_date,
                value: Number(d.output_qty),
                title: `${day(d.business_date)} · ${fmtQty(d.output_qty, 0)} kg out of ${fmtQty(d.input_qty, 0)} kg in${
                  d.yield_pct === null ? "" : ` · ${fmtPct(d.yield_pct)} yield`
                }`,
              }))}
            />
          </section>

          <section className="card">
            <h3 className="section-title">Every machine on the line</h3>
            <p className="section-note">
              All {board.machines.length} configured, whether they ran or not —
              a granulator that never started cannot show up in a table built
              from its own output
            </p>
            <SheetToolbar sheet={machines} exportName="recycling-machines" />
            <div className="tablewrap">
              <table className="table">
                <SheetHead sheet={machines} />
                <tbody>
                  {machines.rows.map((m) => (
                    <tr key={m.machine} className={m.idle ? "row--muted" : ""}>
                      <td>
                        {m.machine}
                        {m.idle && (
                          <span className="cell__unit">idle all period</span>
                        )}
                      </td>
                      <td className="cell__unit">
                        {m.stage.replace(/_/g, " ").toLowerCase()}
                      </td>
                      <td className="num mono">{m.runs || "—"}</td>
                      <td className="num mono">{fmtQty(m.input_qty, 0)}</td>
                      <td className="num mono">{fmtQty(m.output_qty, 0)}</td>
                      <td className="num mono">
                        {m.yield_pct === null ? "—" : fmtPct(m.yield_pct)}
                      </td>
                      <td className="num mono">
                        {fmtQty(m.declared_loss, 0)}
                      </td>
                      <td
                        className={`num mono${
                          Number(m.variance_qty) > 0 ? " cell--negative" : ""
                        }`}
                      >
                        {fmtQty(m.variance_qty, 0)}
                      </td>
                      <td className="num mono">
                        {m.kg_per_hour === null
                          ? "—"
                          : fmtQty(m.kg_per_hour, 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3}>Column total</td>
                    <td className="num mono">{fmtQty(board.total_input, 0)}</td>
                    <td className="num mono">
                      {fmtQty(board.total_output, 0)}
                    </td>
                    <td colSpan={2} />
                    <td className="num mono">
                      <strong>{fmtQty(board.total_variance, 0)}</strong>
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="page__note">
              The totals foot the columns above, not the line end to end — the
              stages are a chain, so washing&rsquo;s output is counted again as
              agglo&rsquo;s input. What the line actually produced is{" "}
              {fmtQty(board.granulated, 0)} kg of granulate from{" "}
              {fmtQty(board.granulated_input, 0)} kg fed to granulation.
            </p>
          </section>
        </>
      )}
    </BoardPage>
  );
}
